import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, ChevronDown, Share2 } from "lucide-react";
import { renderInline } from "./renderInline";
import { GuideSection, slugify } from "./GuideSection";
import { ListenButton } from "../ListenButton";
import { shareGuideCard } from "../../utils/guideShareCard";

// Guide card — the North Star card's look & behaviour (collapsible, teaser-able sections + share image) for ANY topic.
const lines = (s) => String(s || "").split("\n").map((l) => l.trim()).filter(Boolean);

// Scan mode: clamp to N lines with a "(cont.)" toggle. Used for every paragraph / bullet when cfg.scan is on.
const Clamp = ({ lines: n = 2, accent, as: Tag = "div", className = "", children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Tag className={className} data-testid={testId}>
      <span className={open ? "" : (n === 1 ? "line-clamp-1" : "line-clamp-2")}>{children}</span>
      <button type="button" onClick={() => setOpen(!open)} className="ml-1 text-[13px] font-extrabold underline underline-offset-2 hover:opacity-80" style={{ color: accent }} data-testid="guide-cont">
        {open ? "(less)" : "(cont.)"}
      </button>
    </Tag>
  );
};
const isExt = (u) => /^https?:/i.test(u || "");

const SectionBody = ({ s, accent, scan = false }) => {
  const Para = ({ children, className = "", lines: n = 1 }) => scan ? <Clamp lines={n} accent={accent} as="p" className={className}>{children}</Clamp> : <p className={className}>{children}</p>;
  const Item = ({ children }) => scan ? <Clamp lines={2} accent={accent} as="li">{children}</Clamp> : <li>{children}</li>;
  const kind = s.kind || "text";
  const H = ({ children }) => <h3 className="text-base md:text-xl font-extrabold uppercase tracking-[0.15em]" style={{ color: accent }}>{children}</h3>;
  const P = "text-[15px] leading-relaxed text-[#3a3a3a] [&_strong]:text-[#1A1A1A]";
  if (kind === "alert") return (
    <div className="rounded-xl border-l-4 border-[#B91C1C] bg-[#FEF2F2] px-5 py-4">
      <h3 className="text-sm md:text-base font-extrabold uppercase tracking-[0.15em] text-[#B91C1C]">{s.title}</h3>
      <p className={`mt-2 ${P} [&_strong]:text-[#B91C1C]`}>{renderInline(s.body, accent)}</p>
    </div>
  );
  if (kind === "stat") return (
    <div>
      <H>{s.title}</H>
      <div className="mt-3 flex flex-col sm:flex-row gap-4 sm:items-start">
        <div className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold leading-none shrink-0" style={{ color: accent }}>{s.stat}</div>
        <div className={`space-y-3 ${P}`}>{lines(s.body).map((l, i) => <p key={i}>{renderInline(l, accent)}</p>)}</div>
      </div>
    </div>
  );
  if (kind === "quotes") {
    const [first, ...closing] = String(s.body || "").split(/\n\s*\n/);
    const [intro, ...quotes] = lines(first);
    return (
      <div>
        <H>{s.title}</H>
        {intro && <p className={`mt-3 ${P}`}>{renderInline(intro, accent)}</p>}
        <div className="mt-3 pl-5 border-l-2 space-y-1.5 italic text-[#5C5C5C] text-[15px]" style={{ borderColor: `${accent}55` }}>{quotes.map((q, i) => <p key={i}>“{q}”</p>)}</div>
        {closing.map((c, i) => <p key={i} className={`mt-3 ${P}`}>{renderInline(c.trim(), accent)}</p>)}
      </div>
    );
  }
  if (kind === "places") {
    const [intro, ...closing] = String(s.body || "").split(/\n\s*\n/);
    return (
      <div>
        <H>{s.title}</H>
        <div className={`mt-3 space-y-3 ${P}`}>{lines(intro).map((l, i) => <p key={i}>{renderInline(l, accent)}</p>)}</div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="guide-places-grid">
          {(s.places || []).map((p, i) => (
            <article key={i} className="rounded-xl border bg-[#FBFAF8] p-4 flex flex-col gap-2" style={{ borderColor: `${accent}33` }} data-testid={`guide-place-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-['Playfair_Display'] text-lg font-bold text-[#1A1A1A] leading-tight">{p.name}</h4>
                {p.rating && <span className="shrink-0 text-xs font-extrabold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: accent }}>★ {p.rating}</span>}
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.12em]" style={{ color: accent }}>{[p.tag, p.where].filter(Boolean).join(" · ")}</p>
              {p.soak && <p className="text-[14px] text-[#3a3a3a]"><strong className="text-[#1A1A1A]">The soak:</strong> {renderInline(p.soak, accent)}</p>}
              {p.healing && <p className="text-[14px] text-[#3a3a3a]"><strong className="text-[#1A1A1A]">The healing:</strong> {renderInline(p.healing, accent)}</p>}
              {p.note && <p className="text-[13px] text-[#6b6b6b] italic">{renderInline(p.note, accent)}</p>}
              {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-auto text-sm font-extrabold underline" style={{ color: accent }}>{p.link_label || "Visit →"}</a>}
            </article>
          ))}
        </div>
        {closing.map((c, i) => <p key={i} className={`mt-4 ${P}`}>{renderInline(c.trim(), accent)}</p>)}
      </div>
    );
  }
  if (kind === "bullets" || kind === "numbered") {
    const L = kind === "numbered" ? "ol" : "ul";
    return (
      <div>
        <H>{s.title}</H>
        <L className={`mt-3 space-y-2 ${P} ${kind === "numbered" ? "list-decimal" : "list-disc"} pl-5`}>{lines(s.body).map((l, i) => <Item key={i}>{renderInline(l, accent)}</Item>)}</L>
      </div>
    );
  }
  if (scan) {
    const ls = lines(s.body);
    return (
      <div>
        <H>{s.title}</H>
        <div className={`mt-3 ${P}`}>
          <Clamp lines={1} accent={accent} as="div" className="space-y-3">{ls.map((l, i) => <p key={i} className={i > 0 ? "mt-3" : ""}>{renderInline(l, accent)}</p>)}</Clamp>
        </div>
      </div>
    );
  }
  return (
    <div>
      <H>{s.title}</H>
      <div className={`mt-3 space-y-3 ${P}`}>{lines(s.body).map((l, i) => <p key={i}>{renderInline(l, accent)}</p>)}</div>
    </div>
  );
};

export const GuideCardBlock = ({ block, festival }) => {
  const cfg = block.config || {};
  const accent = cfg.accent || "#1D4ED8";
  const [open, setOpen] = useState(Boolean(cfg.open_by_default));
  const [sharing, setSharing] = useState(false);
  const sections = (cfg.sections || []).filter((s) => !s.hidden && (s.title || s.body));
  useEffect(() => {
    const go = () => {
      const h = window.location.hash.replace("#", "");
      if (h && (h === cfg.anchor || sections.some((s) => slugify(s.title) === h))) {
        setOpen(true);
        setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
    };
    go();
    window.addEventListener("hashchange", go);
    return () => window.removeEventListener("hashchange", go);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const title = cfg.title || block.title || festival?.title || "Guide";
  const spec = {
    color: accent,
    brand: cfg.share_brand || `THE WALL · ${title}`,
    eyebrow: cfg.eyebrow || "",
    title,
    lede: cfg.lede || block.quip || "",
    alert_title: cfg.share_alert_title || sections.find((s) => s.kind === "alert")?.title || "",
    alert_body: cfg.share_alert_body || sections.find((s) => s.kind === "alert")?.body || "",
    tell_label: cfg.share_tell_label || "Remember",
    tell_body: cfg.share_tell_body || "",
    footer_line: cfg.share_footer_line || "",
    link_line: cfg.share_link_line || `Learn at THE WALL: LOOKWASHINGTON.com${typeof window !== "undefined" ? window.location.pathname : ""}`,
  };
  const share = async () => {
    setSharing(true);
    try {
      const r = await shareGuideCard(spec, `${(festival?.slug || "guide")}-card.png`, `${title} — ${spec.link_line}`);
      if (r.method === "native") toast.success("Shared — thank you");
      else if (r.method === "download") toast.success("Card saved — post it wherever your people are");
    } catch { toast.error("Couldn't build the card — try again"); }
    setSharing(false);
  };
  const linkUrl = cfg.link_url || "";
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-12 py-6">
      <section id={cfg.anchor || block._uid || "guide"} className="scroll-mt-24 rounded-2xl border-2 bg-white overflow-hidden" style={{ borderColor: `${accent}4d`, boxShadow: `0 18px 50px -24px ${accent}73` }} data-testid="guide-card">
        <div className="px-5 sm:px-8 py-6 sm:py-7">
          {cfg.eyebrow && <div className="flex items-start gap-2.5 text-sm sm:text-base md:text-lg font-extrabold uppercase tracking-[0.18em] leading-snug" style={{ color: accent }} data-testid="guide-card-eyebrow"><ShieldAlert className="h-5 w-5 md:h-6 md:w-6 shrink-0 mt-0.5" /> <span>{cfg.eyebrow}</span></div>}
          <h2 className="mt-3 font-['Playfair_Display'] text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-[#1A1A1A]" data-testid="guide-card-title">{title}</h2>
          {String(cfg.lede || block.quip || "").split(/\n\s*\n/).filter(Boolean).map((para, i) => (
            <p key={i} className="mt-3 text-base md:text-lg leading-relaxed text-[#3a3a3a] max-w-3xl [&_strong]:text-[#1A1A1A]" data-testid={i === 0 ? "guide-card-lede" : undefined}>{renderInline(para.trim(), accent)}</p>
          ))}
          {cfg.hope && (
            <blockquote className="mt-5 rounded-xl border-l-4 px-5 py-4 max-w-3xl" style={{ borderColor: accent, backgroundColor: `${accent}0f` }} data-testid="guide-card-hope">
              <p className="font-['Playfair_Display'] text-lg md:text-xl italic leading-relaxed text-[#1A1A1A] [&_strong]:not-italic" data-testid="guide-card-hope-text">{renderInline(cfg.hope, accent)}</p>
            </blockquote>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {sections.length > 0 && (
              <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
                className="inline-flex items-center gap-2 rounded-full bg-white border-2 px-7 py-3.5 text-base md:text-lg font-extrabold transition-transform hover:-translate-y-0.5"
                style={{ color: accent, borderColor: `${accent}55`, boxShadow: `0 12px 32px -10px ${accent}73` }} data-testid="guide-card-more">
                {open ? "LESS" : (cfg.more_label || "MORE")} <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
            {cfg.share_enabled !== false && spec.footer_line && (
              <button type="button" onClick={share} disabled={sharing} className="inline-flex items-center gap-2 rounded-full text-white px-5 py-3 text-sm md:text-base font-extrabold disabled:opacity-60" style={{ backgroundColor: accent }} data-testid="guide-card-share">
                <Share2 className="h-4 w-4" /> {sharing ? "Building card…" : (cfg.share_label || "Share this card")}
              </button>
            )}
          </div>
        </div>
        {open && (
          <div className="border-t border-[#E2E0DC] px-5 sm:px-8 py-7 space-y-8" style={{ backgroundColor: `${accent}0d` }} data-testid="guide-card-body">
            {sections.map((s, i) => (
              <div key={i} className="space-y-8">
                <GuideSection section={s} accent={accent} testKey={i} listen={<ListenButton blockId={block.block_id} section={i} accent={accent} />}><SectionBody s={s} accent={accent} scan={Boolean(cfg.scan)} /></GuideSection>
                {cfg.cta_url && cfg.cta_every > 0 && (i + 1) % cfg.cta_every === 0 && i < sections.length - 1 && (
                  <Link to={cfg.cta_url} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold text-white no-underline hover:brightness-110" style={{ backgroundColor: cfg.cta_color || accent }} data-testid={`guide-inline-cta-${i}`}>{cfg.cta_label || "Learn more →"}</Link>
                )}
              </div>
            ))}
            {(linkUrl || cfg.disclaimer) && (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {linkUrl && (isExt(linkUrl)
                  ? <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full text-white px-6 py-3 text-sm md:text-base font-extrabold" style={{ backgroundColor: accent }} data-testid="guide-card-link">{cfg.link_label || "Learn more →"}</a>
                  : <Link to={linkUrl} className="inline-flex items-center gap-2 rounded-full text-white px-6 py-3 text-sm md:text-base font-extrabold" style={{ backgroundColor: accent }} data-testid="guide-card-link">{cfg.link_label || "Learn more →"}</Link>)}
                {cfg.disclaimer && <Link to="/disclaimer" className="text-xs underline text-[#5C5C5C]" data-testid="guide-card-disclaimer">{cfg.disclaimer}</Link>}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default GuideCardBlock;
